/**
 * Lookup Results Page Route
 * Displays order and shipment information after verification
 */

import { useEffect } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Truck,
  Clock,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { clearOrderSession, fetchOrderData } from '@/lib/api-client';
import type { Shipment } from '@/api/schemas';
import { trackOrderLookupSuccess, trackShipmentClicked } from '@/lib/analytics-client';
import { z } from 'zod';
import { LookupResultsSkeleton } from '@/components/ui/Skeleton';
import { buildSupportHref } from '@/lib/support';
import {
  formatCarrier,
  formatShipmentStatus,
  getShipmentStatusColor,
  getShipmentStatusIcon,
} from '@/lib/shipment-status';
import { formatDate } from '@/lib/date-format';

// Search params validation
const searchParamsSchema = z.object({
  order: z.string().optional(),
});

export const Route = createFileRoute('/lookup/results')({
  validateSearch: searchParamsSchema,
  component: LookupResultsPage,
});

function getResultsErrorCopy({
  order,
  isUnauthorized,
  error,
}: {
  order?: string;
  isUnauthorized: boolean;
  error: Error | null;
}) {
  if (!order) {
    return {
      title: 'Order Link Invalid',
      message:
        'This link is missing the order number. Please start a new lookup with your email and order number.',
    };
  }

  if (isUnauthorized) {
    return {
      title: 'Session Expired',
      message:
        'Your order session expired after a period of inactivity. Please look up your order again to continue.',
    };
  }

  const errorMessage = error?.message.toLowerCase() || '';
  const isNotFound = errorMessage.includes('order not found');

  if (!error || isNotFound) {
    return {
      title: 'Order Not Found',
      message:
        'We could not find that order. Double-check the email address and order number from your confirmation email.',
    };
  }

  return {
    title: 'Unable to Load Order',
    message:
      'We could not load your order right now. Try again in a moment, or contact support if the problem continues.',
  };
}

function ShipmentCard({ shipment }: { shipment: Shipment }) {
  const StatusIcon = getShipmentStatusIcon(shipment.status);
  const statusStyle = getShipmentStatusColor(shipment.status);

  return (
    <div className="bg-card rounded-lg shadow-lg p-6 border border-border theme-transition">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${statusStyle}`}>
            <StatusIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="font-medium text-foreground">{formatShipmentStatus(shipment.status)}</p>
            <p className="text-sm text-foreground-muted">
              {shipment.carrier ? formatCarrier(shipment.carrier) : 'Carrier pending'}
            </p>
          </div>
        </div>

        {shipment.trackingNumber && (
          <Link
            to="/track/$trackingNumber"
            params={{ trackingNumber: shipment.trackingNumber }}
            onClick={() => {
              void trackShipmentClicked(shipment.carrier ?? null);
            }}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-fathom-gold hover:text-fathom-gold-light border border-fathom-gold rounded-lg hover:bg-fathom-gold/5 transition-colors"
          >
            Track Package
            <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>

      {/* Tracking Number */}
      {shipment.trackingNumber && (
        <div className="mb-4 p-3 bg-background-tertiary rounded-lg">
          <p className="text-xs text-foreground-muted mb-1">Tracking Number</p>
          <p className="font-mono text-sm font-medium text-foreground">
            {shipment.trackingNumber}
          </p>
        </div>
      )}

      {/* Route Info */}
      <div className="flex items-center text-sm text-foreground-muted">
        <span>
          {shipment.fromCity}, {shipment.fromState}
        </span>
        <span className="mx-2">→</span>
        <span>
          {shipment.toCity}, {shipment.toState}
        </span>
      </div>

      {/* Dates */}
      <div className="mt-4 pt-4 border-t border-border-subtle grid grid-cols-2 gap-4 text-sm">
        {shipment.shippedAt && (
          <div>
            <p className="text-foreground-muted">Shipped</p>
            <p className="font-medium text-foreground">
              {formatDate(shipment.shippedAt, 'MMM d, yyyy')}
            </p>
          </div>
        )}
        {shipment.deliveredAt ? (
          <div>
            <p className="text-foreground-muted">Delivered</p>
            <p className="font-medium text-green-600 dark:text-green-400">
              {formatDate(shipment.deliveredAt, 'MMM d, yyyy')}
            </p>
          </div>
        ) : shipment.estimatedDeliveryDate ? (
          <div>
            <p className="text-foreground-muted">Est. Delivery</p>
            <p className="font-medium text-foreground">
              {formatDate(shipment.estimatedDeliveryDate, 'MMM d, yyyy')}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LookupResultsPage() {
  const { order } = Route.useSearch();
  const supportHref = buildSupportHref({ kind: 'order', orderNumber: order });

  useEffect(() => {
    document.title = 'Your Order - Fathom Coffee';
  }, []);

  const {
    data: orderData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['order', order],
    queryFn: () => {
      if (!order) throw new Error('No order number provided');
      return fetchOrderData(order);
    },
    enabled: !!order,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry on auth errors
  });

  useEffect(() => {
    if (!orderData) return;

    void trackOrderLookupSuccess(orderData.orderNumber, orderData.shipments.length);
  }, [orderData]);

  const navigate = useNavigate();

  const isUnauthorized =
    error instanceof Error && /verify your order first/i.test(error.message);

  // On auth/session expiry, clear server session and redirect to /lookup?expired=true
  useEffect(() => {
    if (!isUnauthorized) return;
    let cancelled = false;
    void (async () => {
      try {
        await clearOrderSession();
      } catch {
        // best-effort — proceed with redirect regardless
      }
      if (cancelled) return;
      void navigate({ to: '/lookup', search: { expired: true } });
    })();
    return () => {
      cancelled = true;
    };
  }, [isUnauthorized, navigate]);

  if (isLoading) {
    return <LookupResultsSkeleton />;
  }

  if (error || !orderData) {
    const { title, message } = getResultsErrorCopy({
      order,
      isUnauthorized,
      error: error instanceof Error ? error : null,
    });

    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div
          role="alert"
          className="bg-card rounded-lg shadow-lg p-8 border border-border theme-transition"
        >
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold text-foreground mb-2">
            {title}
          </h2>
          <p className="text-foreground-muted mb-6">{message}</p>
          <Link
            to="/lookup"
            className="inline-block px-6 py-3 bg-fathom-gold text-white font-medium rounded-lg hover:bg-fathom-gold-light transition-colors"
          >
            Look Up Order Again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Order Header */}
      <div className="bg-card rounded-lg shadow-lg p-6 mb-6 border border-border theme-transition">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-foreground-muted">Order</p>
            <p className="text-2xl font-heading font-bold text-foreground">
              #{orderData.orderNumber}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-foreground-muted">Placed on</p>
            <p className="text-lg font-medium text-foreground">
              {formatDate(orderData.orderDate, 'MMMM d, yyyy')}
            </p>
          </div>
        </div>
      </div>

      {/* Shipments */}
      <div className="space-y-4">
        <h2 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
          <Truck className="w-5 h-5 text-fathom-gold" />
          Shipments ({orderData.shipments.length})
        </h2>

        {orderData.shipments.length === 0 ? (
          <div className="bg-card rounded-lg shadow-lg p-8 text-center border border-border theme-transition">
            <Clock className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
            <p className="text-foreground-muted">
              No shipments yet. Your order is still being prepared, which is normal for fresh
              orders.
            </p>
            <p className="mt-2 text-sm text-foreground-muted">
              If you expected a shipment already, check back soon or contact{' '}
              <a href={supportHref} className="text-fathom-blue hover:underline">
                support
              </a>{' '}
              with your order number.
            </p>
          </div>
        ) : (
          orderData.shipments.map((shipment) => (
            <ShipmentCard key={shipment.id} shipment={shipment} />
          ))
        )}
      </div>

      {/* Actions */}
      <div className="mt-8 text-center space-y-4">
        <Link
          to="/lookup"
          className="inline-block px-6 py-3 border-2 border-foreground text-foreground font-medium rounded-lg hover:bg-foreground hover:text-background transition-colors"
        >
          Look Up Another Order
        </Link>
        <p className="text-sm text-foreground-muted">
          Need help?{' '}
          <a href={supportHref} className="text-fathom-blue hover:underline">
            Contact us
          </a>
          . Include your order number and the email address used at checkout.
        </p>
      </div>
    </div>
  );
}
